import {
  Link,
  Text,
} from '@chakra-ui/react';
import {
  useRoadMaintenanceCost,
} from '@src/modules/performance/context/reports-api-hooks';
import GroupedRegionMessage from '@src/modules/performance/components/GroupedRegionMessage';
import LineBarChart from '@src/modules/performance/components/charts/LineBarChart';
import { CHART_COLOURS } from '@src/modules/performance/shared/constants';
import ReportSection from './ReportSection';

const informationSource = {
  title: 'Road Maintenance Cost',
  content: [
    {
      title: 'Road maintenance cost / lane km ($/lane km)',
      description:
  <>
    <Text fontSize="inherit" mb={2}>This result is calculated by dividing the National Land Transport Fund amount spent on maintenance activites on the road network during the financial year by the total number of lane-kilometres in the network. </Text>
    <Text fontSize="inherit" mb={2}>
      From December 2025 the NLTF amount for State Highways regions will be reported by the lane kilometres in the region rather than the lane kilometres of the full State Highways network.
    </Text>
    <Text fontSize="inherit" mb={2}>
      The NLTF amount includes the following work categories: 111, 112, 113, 114, 121, 122, 123, 124, 125, 131, 140, 151, 161, 211, 212, 213, 214, 215, 216, 221, 222, 224, 225, 231.
    </Text>
  </>,
      source:
  <Text fontSize="inherit" as="span">
    Approved and claimed road maintenance costs sourced from NZTA. Network lane-kms sourced from
    {' '}
    <Link href="https://www.nzta.govt.nz/planning-and-investment/learning-and-resources/transport-data/data-and-tools" isExternal>NZTA, physical statistics - roads.</Link>
  </Text>,
    },
  ],
};

function RoadMaintenanceDeliveryCosts() {
  const { data: roadMaintenanceCost, isLoading } = useRoadMaintenanceCost();

  // Road Maintenance Cost Chart
  const roadMaintenanceCostCategories = [...new Set(roadMaintenanceCost?.map((item) => item.Year))].sort((a, b) => a - b);
  const roadMaintenanceCostData = [{
    name: 'Variance %',
    type: 'line',
    data: roadMaintenanceCostCategories.map((year) => ({
      x: year,
      y: roadMaintenanceCost?.find((item) => item.Year === year)?.Variance,
    })),
    color: CHART_COLOURS.grey,
  },
  {
    name: 'Approved',
    type: 'bar',
    data: roadMaintenanceCostCategories.map((year) => ({
      x: year,
      y: roadMaintenanceCost?.find((item) => item.Year === year)?.Forecast || null,
    })),
    color: CHART_COLOURS.blue,
  },
  {
    name: 'Claimed',
    type: 'bar',
    data: roadMaintenanceCostCategories.map((year) => ({
      x: year,
      y: roadMaintenanceCost?.find((item) => item.Year === year)?.Achieved || null,
    })),
    color: CHART_COLOURS.red,
  },
  ];

  return (
    <ReportSection
      title="Road Maintenance Costs"
      isLoading={isLoading}
      lockYear={roadMaintenanceCost?.[0]?.LockYear}
      informationSource={informationSource}
    >
      <LineBarChart
        title="Road maintenance cost / lane km ($)"
        categories={roadMaintenanceCostCategories}
        series={roadMaintenanceCostData}
        width="280"
        height="280"
        leftAxisLabel="$/lane km"
        rightAxisLabel="Variance %"
        barSeriesName="Approved"
        lineSeriesName="Variance %"
        barSeriesFormatter={(y) => {
          if (typeof y !== 'undefined' && y !== null) {
            return `${y.toLocaleString('en-NZ', {
              style: 'currency',
              currency: 'NZD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).replace('NZD', '')}`;
          }
          return y;
        }}
        lineSeriesFormatter={(y) => (typeof y !== 'undefined' ? `${y.toFixed(0)}%` : '')}
      />
      <GroupedRegionMessage
        rcaIds={[1014, 1016]}
        roadTypes={['shlr', 'sh']}
        messagePrefix="Combined results for"
        messageSuffix="for State Highways and All Roads"
      />
    </ReportSection>
  );
}

export default RoadMaintenanceDeliveryCosts;
